import React, { useMemo } from "react";

// Icons
import SettingsOverscanIcon from "@material-ui/icons/SettingsOverscan";
import { Delete } from "src/icons/Delete";
import { PencilUnderlined } from "src/icons/PencilUnderlined";
import { Run } from "src/icons/Run";

// Utils
import { dateFormat } from "src/utils/dateFormat";
import { getScenarioStatusTemplate } from "../utils/Scenarios.helpers";

// Components
import { OverflowTooltip, Table } from "src/components";
import { LinkCellWithMenu } from "src/components/Table/Cells/LinkCellWithMenu/LinkCellWithMenu.component";

// Constants
import { ScenariosHelperText } from "../utils/Scenarios.constants";

type Props = {
  data: any;
  isRefetchingProjectCanvas?: boolean;
  isRecipesInProject?: boolean;
  isRecipesRunningAcrossScenariosStore?: boolean;
  isConnectorDatasets?: boolean;
  onEdit: (segmentId?: string) => void;
  onDelete: (segmentId?: string) => void;
  onRun: (segmentId?: string, reloadDatasets?: boolean) => void;
  onDisplayOnDag: (segmentId?: string) => void;
  searchValue: string;
};

const ScenariosTable = (props: Props) => {
  const {
    data,
    isRefetchingProjectCanvas,
    isRecipesInProject,
    isRecipesRunningAcrossScenariosStore,
    isConnectorDatasets,
    onEdit,
    onDelete,
    onRun,
    onDisplayOnDag,
    searchValue
  } = props || {};

  const disabledRunActionMessage = useMemo(() => {
    if (!!isRefetchingProjectCanvas) {
      return ScenariosHelperText.DataFetchingInfo;
    }

    if (!isRecipesInProject) {
      return ScenariosHelperText.NoRecipesInProjectInfo;
    }

    if (!!isRecipesRunningAcrossScenariosStore) {
      return ScenariosHelperText.RecipesRunningInfo;
    }

    return "";
  }, [isRefetchingProjectCanvas, isRecipesInProject, isRecipesRunningAcrossScenariosStore]);

  const disabledRunWithReloadDatasetsActionMessage = useMemo(() => {
    if (!!isRefetchingProjectCanvas) {
      return ScenariosHelperText.DataFetchingInfo;
    }

    if (!isRecipesInProject) {
      return ScenariosHelperText.NoRecipesInProjectInfo;
    }

    if (!isConnectorDatasets) {
      return ScenariosHelperText.ConnectorDatasetsInfo;
    }

    if (!!isRecipesRunningAcrossScenariosStore) {
      return ScenariosHelperText.RecipesRunningInfo;
    }

    return "";
  }, [
    isRefetchingProjectCanvas,
    isRecipesInProject,
    isConnectorDatasets,
    isRecipesRunningAcrossScenariosStore
  ]);

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        id: "Name",
        accessor: "name",
        isTooltip: false,
        Cell: ({ row, value }: any) => (
          <LinkCellWithMenu
            linkText={<OverflowTooltip style={{ whiteSpace: "nowrap" }} value={value} />}
            {...(!row?.original?.default ? { onClick: () => onEdit(row?.original?.id) } : {})}
            menuButtons={[
              {
                icon: <PencilUnderlined />,
                label: ScenariosHelperText.Edit,
                action: () => onEdit(row?.original?.id),
                ...(!!row?.original?.default
                  ? {
                      disabled: true,
                      tooltip: ScenariosHelperText.DefaultScenarioRestrictedEditInfo
                    }
                  : {})
              },
              {
                icon: <Run />,
                label: ScenariosHelperText.Run,
                disabled: !!disabledRunActionMessage,
                action: () => onRun(row?.original?.id),
                ...(!!disabledRunActionMessage
                  ? { tooltip: disabledRunActionMessage }
                  : { tooltip: ScenariosHelperText.RunActionInfo })
              },
              {
                icon: <Run />,
                label: ScenariosHelperText.RunWithReloadDatasets,
                disabled: !!disabledRunWithReloadDatasetsActionMessage,
                action: () => onRun(row?.original?.id, true),
                ...(!!disabledRunWithReloadDatasetsActionMessage
                  ? { tooltip: disabledRunWithReloadDatasetsActionMessage }
                  : { tooltip: ScenariosHelperText.RunWithReloadDatasetsActionInfo })
              },
              {
                icon: <SettingsOverscanIcon />,
                label: ScenariosHelperText.DisplayOnDag,
                action: () => onDisplayOnDag(row?.original?.id)
              },
              {
                icon: <Delete />,
                label: ScenariosHelperText.Delete,
                action: () => onDelete(row?.original?.id),
                ...(!!row?.original?.default
                  ? {
                      disabled: true,
                      tooltip: ScenariosHelperText.DefaultScenarioRestrictedDeleteInfo
                    }
                  : {
                      disabled: !!isRecipesRunningAcrossScenariosStore,
                      tooltip: ScenariosHelperText.RecipesRunningInfo
                    })
              }
            ]}
          />
        )
      },
      {
        Header: "Segments",
        id: "Segments",
        accessor: "segments"
      },
      {
        Header: "Last updated on",
        id: "Last updated on",
        accessor: "updated",
        Cell: ({ value }: any) => <span>{dateFormat(value)}</span>
      },
      {
        Header: "Status",
        id: "Status",
        accessor: "status",
        Cell: ({ value }: any) => getScenarioStatusTemplate(value, true)
      }
    ],
    [disabledRunActionMessage, disabledRunWithReloadDatasetsActionMessage]
  );

  return (
    <Table
      size="small"
      data={data}
      columns={columns}
      globalFilter={searchValue}
      emptyTableMessage="No records"
      orderByDefault="Last updated on"
    />
  );
};

export default ScenariosTable;
